import type { SettingsFlow, UiNode, UiNodeInputAttributes } from '@ory/client';
import type { IconName } from '@/components/Icon/iconMap';
import type { ApiResponseBody } from '@/lib/api/api-type-helper';
import { apiClient } from '@/lib/api/client';
import { getDataOrThrowApiError } from '@/lib/api/error';
import { LANGUAGE_CONFIG, type SupportedLanguage } from '@/lib/i18n/config/languages';
import { oryKratosClient } from '../ory-kratos.client';
import { handleOryErrorWithReauth } from './error-handler';
import { getErrorResponseData, getRedirectBrowserTo, hasHttpStatus, is400Error, isBrowserLocationChangeRequired, type ServiceResponse } from './types';
export type OidcProvider = 'microsoft' | 'google';
export type OidcProviderConfig = {
  provider: OidcProvider;
  iconName: IconName;
};
export const SUPPORTED_OIDC_PROVIDERS: OidcProviderConfig[] = [{
  provider: 'microsoft',
  iconName: 'OsWindowsIcon'
}];
export function resolveProviderLabel(provider: string, labels: Partial<Record<OidcProvider, string>>): string {
  if (provider in labels) {
    return labels[provider as OidcProvider] ?? provider;
  }
  return provider;
}
export function buildProviderLabels(translate: (key: 'microsoft') => string): Partial<Record<OidcProvider, string>> {
  return {
    microsoft: translate('microsoft')
  };
}
export type SsoCredentialItem = ApiResponseBody<'/v2/auth/sso/credentials', 'get'>[number];
function getLocaleShortCode(locale: string): string {
  if (locale in LANGUAGE_CONFIG) {
    return LANGUAGE_CONFIG[locale as SupportedLanguage].shortCode;
  }
  return 'en';
}

/**
 * loginCheckMessages から現在のロケールに対応するメッセージを取得する。
 * ロケールに対応するメッセージがない場合は英語にフォールバックする。
 */
export function getLoginCheckMessage(messages: SsoCredentialItem['loginCheckMessages'], locale: string): string | null {
  if (!messages) return null;
  const shortCode = getLocaleShortCode(locale);
  const msg = messages[shortCode];
  if (typeof msg === 'string') return msg;
  const fallback = messages.en;
  return typeof fallback === 'string' ? fallback : null;
}

/**
 * @ory/client の UpdateSettingsFlowWithOidcMethod に csrf_token が含まれていないため、
 * ブラウザフローで必要な csrf_token を渡せるようローカル型を定義する。
 */
type OidcSettingsFlowBody = {
  method: 'oidc';
  link?: string;
  unlink?: string;
  csrf_token?: string;
};
function isInputNode(node: UiNode): node is UiNode & {
  attributes: UiNodeInputAttributes;
} {
  return node.attributes.node_type === 'input';
}
function findCsrfToken(flow: SettingsFlow): string | undefined {
  const csrfNode = flow.ui.nodes.find(node => isInputNode(node) && node.attributes.name === 'csrf_token');
  return csrfNode && isInputNode(csrfNode) ? csrfNode.attributes.value as string | undefined : undefined;
}
export const ssoSettingsService = {
  async getSsoCredentials(): Promise<SsoCredentialItem[]> {
    const res = await apiClient.GET('/v2/auth/sso/credentials');
    return getDataOrThrowApiError(res);
  },
  async initFlow(options?: {
    returnTo?: string;
  }): Promise<SettingsFlow> {
    const {
      data
    } = await oryKratosClient.getApi().createBrowserSettingsFlow({
      returnTo: options?.returnTo
    });
    if (!data) {
      throw new Error('Failed to create settings flow');
    }
    return data;
  },
  async getFlow(flowId: string): Promise<SettingsFlow> {
    const {
      data
    } = await oryKratosClient.getApi().getSettingsFlow({
      id: flowId
    });
    if (!data) {
      throw new Error('Failed to get settings flow');
    }
    return data;
  },
  /**
   * OIDC link を送信する。
   * Kratos は IdP リダイレクトが必要なため 422 を返す。
   * `redirectTo` に IdP の URL が格納される。
   */
  async submitOidcLink(flowId: string, provider: OidcProvider, csrfToken?: string): Promise<ServiceResponse<SettingsFlow>> {
    try {
      const {
        data
      } = await handleOryErrorWithReauth(async () => {
        return await oryKratosClient.getApi().updateSettingsFlow({
          flow: flowId,
          updateSettingsFlowBody: {
            method: 'oidc',
            link: provider,
            csrf_token: csrfToken
          } as OidcSettingsFlowBody
        });
      });
      return {
        data: data ?? null,
        error: null
      };
    } catch (err) {
      if (isBrowserLocationChangeRequired(err)) {
        const redirectTo = getRedirectBrowserTo(err);
        return {
          data: null,
          error: null,
          redirectTo: redirectTo ?? undefined
        };
      }
      if (is400Error(err)) {
        const flowData = getErrorResponseData<SettingsFlow>(err);
        return {
          data: flowData,
          error: null
        };
      }
      return {
        data: null,
        error: err as Error
      };
    }
  },
  /**
   * OIDC unlink を送信する。
   * unlink は IdP リダイレクト不要なので 200 が返る。
   */
  async submitOidcUnlink(flowId: string, provider: OidcProvider, csrfToken?: string): Promise<ServiceResponse<SettingsFlow>> {
    try {
      const {
        data
      } = await handleOryErrorWithReauth(async () => {
        return await oryKratosClient.getApi().updateSettingsFlow({
          flow: flowId,
          updateSettingsFlowBody: {
            method: 'oidc',
            unlink: provider,
            csrf_token: csrfToken
          } as OidcSettingsFlowBody
        });
      });
      return {
        data: data ?? null,
        error: null
      };
    } catch (err) {
      if (is400Error(err) || hasHttpStatus(err, 409)) {
        const flowData = getErrorResponseData<SettingsFlow>(err);
        return {
          data: flowData,
          error: null,
          isConflict: hasHttpStatus(err, 409)
        };
      }
      return {
        data: null,
        error: err as Error
      };
    }
  },
  getCsrfToken: findCsrfToken
};